/* eslint-disable max-lines-per-function */
/* eslint-disable max-len */
/* eslint-disable complexity */
/* eslint-disable max-statements */
import { Axis } from '../../../spatial-classes';
import { getPanelToIslandResolver } from '../helpers';
import { GetIntersectionAreasOptions } from '../types';
import { IntersectionArea } from '../../shared.types';
import { IslandType } from '../../islands/enums';
import { isVerticalBottomIntersect } from '.';
import { nearestSixteenth } from '../../../helpers';
import { Panel } from '../panel.class';
import { PanelItem } from '../../panel-items/panel-item.class';
import { COMPONENT_PLACEMENT_FRONT_AND_TOP, COMPONENT_PLACEMENT_TOP_ONLY } from '../../../component-classes/constants';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getVerticalIntersectionAreasCfn = (panel: Panel, options: GetIntersectionAreasOptions): Array<IntersectionArea> => {
	const { inside = false, includeDuplicates = true, includeEnd2End = true, minHeight = 0, minWidth = 0, isCountertop = false } = options;
	const panelItems: Array<{ i: number; pi: PanelItem, top: number, bottom: number, left: number, right: number, isCircularCutout: boolean }> = [];
	// console.log('getHorizontalOverlaps', { panel, inside });

	let index = 1;
	const matched: IntersectionArea[] = [];
	// -------------------------------
	// build a list of the panel items that are on the panel
	// -------------------------------
	// The countertop draws every panel's item groups, so it must enumerate every
	// panel's items (a top-only cutout added through the back/side panel otherwise
	// gets no dimensions). The panel-face view still measures only its own panel.
	const itemGroups = isCountertop
		? panel.refMap.island.panels.children.flatMap((itemPanel) => itemPanel.panelItemGroups.children)
		: panel.panelItemGroups.children;
	itemGroups.forEach((currGrp) => {
		currGrp.items.children.forEach((pi) => {
			if (pi.item.category?.isSpacer) return;
			const piCenter = pi.getCenter(inside);
			if ((pi.item.component.dimensions.outside.vertical.ignore && !inside) || (pi.item.component.dimensions.inside.vertical.ignore && inside)) return;
			if (isCountertop && (pi.item.placement === COMPONENT_PLACEMENT_TOP_ONLY || pi.item.placement === COMPONENT_PLACEMENT_FRONT_AND_TOP) && pi.sleeve === null) {
				// MFG (cutout-only) view never draws a non-cutting top-only item, so exclude it from gap matching too, or it occludes the real gap between its neighbors.
				if (inside && pi.item.placement === COMPONENT_PLACEMENT_TOP_ONLY && !pi.item.distanceBelowCountertop) return;

				const { top: corners } = pi.getCorners(inside, true);
				/* topCorners = [front-left, front-right, back-right, back-left] */

				const isUmbrellaCutout = pi.item.sku.startsWith('CUSTOM_UMBRELLA_CT-');
				const isGasLineCutout = pi.item.sku.startsWith('CUSTOM_GASOLINE_CT-') || pi.item.sku === 'GAS_LINE_CUTOUT' || pi.item.label === 'GAS_LINE_CUTOUT';
				const isMokPassthrough = pi.item.sku === 'MOK-PASSTHROUGH';
				const isHoleCategory = pi.item.category?.key === 'hole';
				const isCircularCutout = isUmbrellaCutout || isGasLineCutout || isMokPassthrough || isHoleCategory;
				const groupCenterX = pi.refMap?.panelItemGroup?.center.x ?? 0;

				// Transform panel-local (width, depth) into island/countertop space.
				const itemPanel = pi.refMap?.panel ?? panel;
				const resolveToIsland = getPanelToIslandResolver(itemPanel);
				const toIsland = (localX: number, localZ: number): { x: number; y: number } => {
					const { x, z } = resolveToIsland(localX, localZ);
					return { x, y: z };
				};

				// For circular cutouts the SVG renders at the panel item's own visible
				// center, NOT the corner-derived center (which bakes in `zCenterAdjustment`
				// when `inside=true`). Collapse top=bottom=visibleY for those.
				const overhang = pi.item.placement === COMPONENT_PLACEMENT_FRONT_AND_TOP ? itemPanel.countertopOverhang : 0;
				const leftLocalX = corners[0].x + groupCenterX;
				const rightLocalX = corners[1].x + groupCenterX;
				const backLocalZ = corners[0].y + pi.center.z;
				const frontLocalZ = corners[3].y + pi.center.z + overhang;

				const box = [
					toIsland(leftLocalX, backLocalZ),
					toIsland(rightLocalX, backLocalZ),
					toIsland(rightLocalX, frontLocalZ),
					toIsland(leftLocalX, frontLocalZ)
				];
				const xs = box.map((p) => p.x);
				const ys = box.map((p) => p.y);
				const visible = toIsland((leftLocalX + rightLocalX) / 2, pi.center.z);

				// snap the EDGE, not the span -- keeps countertop dims on the sixteenth grid while still telescoping
				const entry = {
					i: index,
					pi,
					top: isCircularCutout ? nearestSixteenth(visible.y) : nearestSixteenth(Math.min(...ys)),
					bottom: isCircularCutout ? nearestSixteenth(visible.y) : nearestSixteenth(Math.max(...ys)),
					left: Math.min(...xs),
					right: Math.max(...xs),
					isCircularCutout
				};
				panelItems.push(entry);
				index++;
			} else if (!isCountertop && pi.item.placement !== COMPONENT_PLACEMENT_TOP_ONLY && pi.sleeve === null) {
				const { front: corners } = pi.getCorners(inside, false);
				/* frontCorners = [top-left, top-right, bottom-right, bottom-left] */

				// Check label, SKU prefix, and category for circular cutouts
				const isUmbrellaCutout = pi.item.label === 'UMBRELLA-CUTOUT' || pi.item.sku.startsWith('CUSTOM_UMBRELLA');
				const isGasLineCutout = pi.item.label === 'GASOLINE-CUTOUT' || pi.item.label === 'GAS_LINE_CUTOUT' || pi.item.sku.startsWith('CUSTOM_GASOLINE') || pi.item.sku === 'GAS_LINE_CUTOUT';
				const isMokPassthrough = pi.item.sku === 'MOK-PASSTHROUGH';
				const isHoleCategory = pi.item.category?.key === 'hole';
				const isCircularCutout = isUmbrellaCutout || isGasLineCutout || isMokPassthrough || isHoleCategory;
				const centerY = ((corners[0].y + corners[3].y) / 2) + piCenter.y + currGrp.center.y;

				// dimensions measure to the center of the circle
				// snap the EDGE, not the span -- gaps are then differences of sixteenths, so each stays on grid AND they still sum to the overall
				const topY = isCircularCutout
					? nearestSixteenth(centerY)
					: nearestSixteenth(corners[0].y + piCenter.y + currGrp.center.y);
				const bottomY = isCircularCutout
					? nearestSixteenth(centerY)
					: nearestSixteenth(corners[3].y + piCenter.y + currGrp.center.y);

				const entry = {
					i: index,
					pi,
					top: topY,
					bottom: bottomY,
					left: corners[0].x + (pi.refMap?.panelItemGroup?.center.x ?? 0),
					right: corners[1].x + (pi.refMap?.panelItemGroup?.center.x ?? 0),
					isCircularCutout
				};
				// console.log('pi:vertical:entry', entry, corners[0], corners[3], pi.center.y, currGrp.center.y);
				panelItems.push(entry);
				index++;
			}
		});
	});

	// -------------------------------
	// add the panel edges to the list
	// -------------------------------
	let sortedItems = [];

	if (isCountertop) {
		const countertop = panel.refMap.island.getCountertop();
		const countertopCorners = countertop.renderingDetails.vertices;
		const lastIdx = countertopCorners.length - 1;
		const isCorner = panel.refMap.island.type === IslandType.Corner;

		// Corner islands have a V-shaped back edge that can't be represented as a
		// horizontal line. Collapse the back edge to the front edge position so
		// back-to-item gaps produce zero/negative heights and are naturally filtered.
		const backTop = isCorner ? countertopCorners[0].y : countertopCorners[1].y;
		const backBottom = isCorner ? countertopCorners[lastIdx].y : countertopCorners[lastIdx - 1].y;
		const backLeft = isCorner ? countertopCorners[0].x : countertopCorners[1].x;
		const backRight = isCorner ? countertopCorners[lastIdx].x : countertopCorners[lastIdx - 1].x;

		sortedItems = [
			// back edge (collapsed to front for corner islands)
			{ i: 0, pi: countertop.id, top: backTop, bottom: backBottom, left: backLeft, right: backRight },
			// items
			...panelItems.sort((a, b) => a.top - b.top),
			// front edge
			{ i: index, pi: countertop.id, top: countertopCorners[0].y, bottom: countertopCorners[lastIdx].y, left: countertopCorners[0].x, right: countertopCorners[lastIdx].x }
		];
		// console.log('sortedItems', sortedItems, countertopCorners, countertop.center);
	} else {
		const pCorners = inside
			? panel.getCornerPoints(inside, { translate: false, rotate: false, nearestSixteenth: true }, new Set()).corners
			: panel.getSidePoints({ translate: false, rotate: false, nearestSixteenth: true }).corners;
		/* pCorners = [top-left, top-right, bottom-right, bottom-left] */
		// console.log('pCorners', pCorners);
		sortedItems = [
			// top edge
			{ i: 0, pi: panel.id, top: pCorners[0].y, bottom: pCorners[0].y, left: pCorners[0].x, right: pCorners[1].x },
			// items
			...panelItems.sort((a, b) => a.top - b.top),
			// bottom edge
			{ i: index, pi: panel.id, top: pCorners[3].y, bottom: pCorners[3].y, left: pCorners[3].x, right: pCorners[2].x }
		];
		// console.log('sortedItems', sortedItems, pCorners);
	}

	// -------------------------------
	// foreach sortedItem, find everything that overlaps to the right
	sortedItems.forEach((sItem) => {
		let ranges = [sItem];
		let nextRanges = [];

		// find items overlapping to the right of sItem
		const rItems = sortedItems.filter((f) => isVerticalBottomIntersect(sItem, f));
		// console.log(`sItem ${sItem.pi?.item.sku ?? 'null'}-> rItem(s):`, sItem, rItems.map((r) => { return { sku: r.pi?.item.sku, rItem: r }; }));

		for (const nItem of rItems) {
			// console.log(`nItem ${nItem.pi?.item.sku ?? 'null'}`, ranges);
			for (const range of ranges) {
				if (isVerticalBottomIntersect(range, nItem)) {
					// console.log(`sItem ${sItem.pi?.item.sku ?? 'null'} -> ${nItem.pi?.item.sku ?? 'null'}:`, { sItem, nItem, ranges, range });
					const sourceId = typeof sItem.pi === 'string' ? sItem.pi : sItem.pi.id;
					const targetId = typeof nItem.pi === 'string' ? nItem.pi : nItem.pi.id;
					// const sourceId = sItem.pi?.id ?? panel.id;
					// const targetId = nItem.pi?.id ?? panel.id;
					const left = Math.max(nItem.left, range.left);
					const right = Math.min(nItem.right, range.right);
					const size = {
						height: nItem.top - sItem.bottom,
						width: right - left
					};
					const isDuplicate = matched.some((m) => {
						return m.intersectionReferenceIds[0] === sourceId && m.intersectionReferenceIds[1] === targetId;
					});

					// Circular cutouts use center points so width may be 0, but dimensions should still render
					const involvesCircularCutout = sItem.isCircularCutout || nItem.isCircularCutout;

					const widthCheck = involvesCircularCutout ? size.width >= 0 : size.width > 0;
					const heightCheck = involvesCircularCutout ? size.height >= 0 : size.height > 0;
					const minWidthCheck = involvesCircularCutout || minWidth === 0 || size.width >= minWidth;

					// 	piSource: sItem.pi,
					// 	piTarget: nItem.pi,
					// 	top,
					// 	bottom,
					// 	left: sItem.right,
					// 	right: nItem.left,
					// 	height: bottom - top,
					// 	width: nItem.left - sItem.right
					if (heightCheck && widthCheck &&
						(!isDuplicate || (isDuplicate && includeDuplicates)) &&
						(includeEnd2End || (!includeEnd2End && (sourceId !== panel.id || targetId !== panel.id))) &&
						(!includeEnd2End && (targetId !== sourceId)) &&
						(minHeight === 0 || size.height >= minHeight) &&
						minWidthCheck
					) {
						matched.push({
							intersectionReferenceIds: [sourceId, targetId, null],
							axis: new Axis({ dataIn: { z: 90 }}),
							origin: {
								x: left + (size.width / 2),
								y: nItem.top - (size.height / 2)
							},
							size
						} as IntersectionArea);
					}

					if (left > range.left) nextRanges.push({ ...sItem, left: range.left, right: left });
					if (right < range.right) nextRanges.push({ ...sItem, left: right, right: range.right });
					// console.log(`sItem ${sItem.pi?.item.sku ?? 'null'} -> ${nItem.pi?.item.sku ?? 'null'}:`, {
					// 	sItem, nItem, ranges: [...ranges], range, nextRanges: [...nextRanges]
					// });
				} else {
					// push for next appliance check
					nextRanges.push(range);
				}
			}

			// console.log(`overlaps-${sItemIndex}`, { ranges, nextRanges, matched: [...matched] });
			if (nextRanges.length === 0) break;
			ranges = [...nextRanges];
			nextRanges = [];
		}
	});

	// console.log('matched', matched);
	return matched;
};
